// @flow

import { ThemeProvider, css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, graphql, useStaticQuery, withPrefix } from 'gatsby'
import * as React from 'react'
import Helmet from 'react-helmet'

import { useAuth, useSession } from '@toggl/auth'
import { colors, fonts, media, themes } from '@toggl/style'
import {
  Button,
  DropdownBaseTrigger,
  Footer,
  Header,
  Hire,
  IconArrowDownThin,
  IconFacebook,
  IconGitHub,
  IconInstagram,
  IconLinkedIn,
  IconTwitter,
  IconYouTube,
  Navigation,
  Plan,
  Responsive,
  Track,
  text,
  useCloseOnBlurEffect,
  useDropdown,
} from '@toggl/ui'

import { TrackHeaderContents } from './header'
import { LayoutRoot } from './LayoutRoot'

type Props = {
  ...React.ElementProps<typeof LayoutRoot>,
  children?: React.Node,
  background?: string,
  showRatingSchema?: boolean,
  altNav?: boolean,
  hideNav?: boolean,
}

declare var process: WebProcess

const Li = ({ children }) => <li>{children}</li>

const planWidth = css`
  width: 111px;
  svg {
    width: 100%;
  }
`

const hireWidth = css`
  width: 104px;
  svg {
    width: 100%;
  }
`

const IconPlan = () => {
  return (
    <div css={planWidth}>
      <Plan />
    </div>
  )
}

const IconHire = () => {
  return (
    <div css={hireWidth}>
      <Hire />
    </div>
  )
}

const trackWidth = css`
  width: 160px;
  svg {
    width: 100%;
  }
`
const IconTrack = () => {
  return (
    <div css={trackWidth}>
      <Track />
    </div>
  )
}

export const LayoutTrack = ({
  children,
  background,
  showRatingSchema = false,
  altNav = false,
  hideNav = false,
  ...rest
}: Props): React.Element<typeof LayoutRoot> => {
  const data = useStaticQuery(graphql`
    query layoutTrackTrackJobsQueryAndTrackGlobalJobsQuery {
      trackJobs: allJobYaml(
        filter: { listed: { eq: true }, team: { eq: "track" } }
      ) {
        totalCount
      }

      globalJobs: allJobYaml(filter: { listed: { eq: true } }) {
        totalCount
      }
    }
  `)

  const globalPositions = data.globalJobs.totalCount

  return (
    <LayoutRoot {...rest}>
      <Helmet>
        <link
          rel="preconnect"
          href={new URL(process.env.GATSBY_TRACK_APP_URL).origin}
        />
        <link
          rel="preconnect"
          href={new URL(process.env.GATSBY_TRACK_APP_ASSETS_URL).origin}
        />
        <link
          rel="preload"
          as="document"
          href={process.env.GATSBY_TRACK_AUTH_URL}
        />
      </Helmet>

      {showRatingSchema && (
        <Helmet>
          <script type="application/ld+json">{`{
          "@context":"http://schema.org",
          "@type":"SoftwareApplication",
          "name": "Toggl Track",
          "applicationCategory": "BusinessApplication",
          "datePublished": "2019-08-23T08:32:29-08:00",
          "description": "Toggl Track is a time tracking app that allows you to track your daily activities across different platforms; providing you with detailed insights and an opportunity to optimize your workflow by identifying areas where you can improve.",
          "operatingSystem":"Web platform, Windows, Mac OS X, Linux, iOS, Android",
          "url":"https://toggl.com/track/",
          "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
          },
          "image": [
            "https://public-assets.toggl.com/b/static/8500be9b3ef28cfb9293446ddd6f28a3/feature-timers.png",
            "https://public-assets.toggl.com/b/static/589e686d0d91c8608a269963a9f7a667/feature-timeline.png",
            "https://public-assets.toggl.com/b/static/3280573d6e47c9bbb51deb09a91a32e7/feature-calendar.png",
            "https://public-assets.toggl.com/b/static/3564bf4f714c41ebf64c711e2532c2e3/feature-autotracker.png"
          ],
          "featureList":"https://toggl.com/track/features/",
          "screenshot": "https://public-assets.toggl.com/b/static/3c080da045d0117b821dc255d1677d6c/feature-time-reports.png",
          "softwareHelp": "https://support.toggl.com/en/",
          "author":{
              "@type":"Organization",
              "name":"Toggl Track",
              "url":"https://toggl.com/track/",
              "logo":{
                "@type":"ImageObject",
                "url":"https://toggl.com/blog/wp-content/uploads/2020/09/toggl-track_logotype_horizontal_pink_rgb-e1599116263835.png",
                "width":"800px",
                "height":"159px"
              }
          },
          "video":{
              "@type":"VideoObject",
              "caption":"Toggl Track Time Tracker Overview",
              "thumbnailUrl":"https://i.ytimg.com/an_webp/YyILWl6Z17k/mqdefault_6s.webp?du=3000&sqp=CKfhiYEG&rs=AOn4CLCtGucpQfFiUjgpY7vee5E7oW5NIA",
              "contentUrl":"https://www.youtube.com/watch?v=YyILWl6Z17k&t=1s&ab_channel=TogglTrack",
              "name":"Toggl Track Explainer Video",
              "description":"Toggl Track is a time tracking tool that slots into any workflow. We make time tracking easy so teams can focus on the work that matters to them—and pays.",
              "uploadDate":"2020-12-08"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "4627"
          },
          "review": [
            {
              "@type": "Review",
              "author":
              {
                "@type": "Person",
                "name": "Matt"
              },
              "datePublished": "2019-10-23",
              "description": "Love this app. It works seamlessly across the desktop and browser apps. The Pomodoro timer feature is awesome!",
              "name": "Great App for Focus and Productivity",
              "reviewRating": {
                "@type": "Rating",
                "bestRating": "5",
                "ratingValue": "5",
                "worstRating": "1"
              }
            },
            {
              "@type": "Review",
              "author":
              {
                "@type": "Person",
                "name": "Jaime Andres"
              },
              "datePublished": "2019-11-12",
              "description": "Muy recomendada para llevar un control de tiempos con respecto a una actividad y poder relacionarla con algún proyecto, empresa o cliente, gran sincronización y lo mejor de todo es que los reportes que se pueden personalizar.",
              "name": "Genial",
              "reviewRating": {
                "@type": "Rating",
                "bestRating": "5",
                "ratingValue": "5",
                "worstRating": "1"
              }
            }
          ]
        }`}</script>
        </Helmet>
      )}

      <ThemeProvider theme={themes.track}>
        {!hideNav && (
          <Navigation.DropdownProvider>
            <TrackHeader altNav={altNav} background={background} />
          </Navigation.DropdownProvider>
        )}

        {children}
        <Footer.Wrapper
          Link={Link}
          openPositions={globalPositions}
          logo={<IconTrack />}
          products={[
            <>
              <PlanLink href={withPrefix('/plan/')} key="plan">
                <IconPlan />
                <span>Beautifully simple work planning</span>
              </PlanLink>
            </>,
            <>
              <HireLink href={withPrefix('/hire/')} key="hire">
                <IconHire />
                <span>Intelligent candidate-screening</span>
              </HireLink>
            </>,
          ]}
        >
          <Footer.List title="Product">
            <Li>
              <Link to={'/track/features/'}>Features</Link>
            </Li>
            <Li>
              <Link to={'/track/pricing/'}>Pricing</Link>
            </Li>
            <Li>
              <Link to={'/track/integrations/'}>Integrations</Link>
            </Li>
            <Li>
              <Link to={'/track/customers/'}>Case Studies</Link>
            </Li>
            <Li>
              {/** TODO */}
              <a
                href={
                  'https://github.com/toggl/toggl_api_docs/blob/master/toggl_api.md'
                }
                target="_blank"
                rel="noreferrer"
              >
                API
              </a>
            </Li>
          </Footer.List>
          <Footer.List title="Use Cases">
            <Li>
              <Link to={'/track/time-tracking-invoicing/'}>
                Billing and Invoicing
              </Link>
            </Li>
            <Li>
              <Link to={'/track/employee-time-tracking/'}>
                Employee Time Tracking
              </Link>
            </Li>
            <Li>
              <Link to={'/track/project-time-tracking/'}>
                Project Budgeting
              </Link>
            </Li>
            <Li>
              <Link to={'/track/online-time-reporting/'}>Reporting</Link>
            </Li>
            <Li>
              <Link to={'/track/payroll-hours-tracker/'}>Payroll</Link>
            </Li>
          </Footer.List>
          <Footer.List title="Download">
            <Li>
              <Link to={'/track/mobile-time-tracking-app/'}>Mobile Apps</Link>
            </Li>
            <Li>
              <Link to={'/track/toggl-desktop/'}>Desktop Apps</Link>
            </Li>
            <Li>
              <Link to={'/track/integrations/'}>Browser Extensions</Link>
            </Li>
          </Footer.List>
          <Footer.List title="Help">
            <Li>
              <a
                href={'https://support.toggl.com/en/'}
                target="_blank"
                rel="noreferrer"
              >
                Support & Knowledge Base
              </a>
            </Li>
            <Li>
              <Link to={'/track/demo-request/?source=footer'}>
                Request A Demo
              </Link>
            </Li>
            <Li>
              <Link to={'/track/contact/'}>Contact Us</Link>
            </Li>
          </Footer.List>
          <Footer.List title="Resources">
            <Li>
              <Link to={'/track/time-management/'}>Time Management Hub</Link>
            </Li>
            <Li>
              <a href={withPrefix('/work-from-home/')}>Work From Home Hub</a>
            </Li>
            <Li>
              <Link to={'/track/business-resources/'}>Business Resources</Link>
            </Li>
            <Li>
              <Link to={'/track/productivity-resources/'}>
                Productivity Resources
              </Link>
            </Li>
            <Li>
              <Link to={'/track/media-toolkit/'}>Media Kit</Link>
            </Li>
          </Footer.List>
          <Social>
            <RowWrapper>
              <a
                href="https://twitter.com/toggltrack"
                target="_blank"
                rel="noreferrer"
              >
                <IconTwitter />
              </a>
              <a
                href="https://www.facebook.com/TogglTrack/"
                target="_blank"
                rel="noreferrer"
              >
                <IconFacebook />
              </a>
              <a
                href="https://www.instagram.com/toggltrack"
                target="_blank"
                rel="noreferrer"
              >
                <IconInstagram />
              </a>
              <a
                href="https://www.youtube.com/toggl"
                target="_blank"
                rel="noreferrer"
              >
                <IconYouTube />
              </a>
              <a
                href="https://www.linkedin.com/company/toggltrack"
                rel="noreferrer"
                target="_blank"
              >
                <IconLinkedIn />
              </a>
              <a
                href="https://github.com/toggl"
                target="_blank"
                rel="noreferrer"
              >
                <IconGitHub />
              </a>
            </RowWrapper>
          </Social>
        </Footer.Wrapper>
      </ThemeProvider>
    </LayoutRoot>
  )
}

const TrackHeader = ({ altNav, background }) => {
  const headerRef = React.useRef(null)
  const [loggingOut, setLoggingOut] = React.useState(false)
  const [hasSession, session] = useSession()
  const { isOpen, close: closeHeader } = useDropdown()
  const [ready, auth] = useAuth()

  const onLogout = () => {
    // Retry until it's ready
    if (!ready) {
      setTimeout(onLogout, 1000)
    }

    setLoggingOut(true)
    auth
      .logout()
      .then(() => {
        session.destroy()
        setLoggingOut(false)
        return
      })
      .catch(() => {
        setLoggingOut(false)
      })
  }

  useCloseOnBlurEffect(headerRef, closeHeader)

  return (
    <Header ref={headerRef}>
      <MiniBar>
        <MiniBarLinksSection>
          <MiniBarLink to={'/'}>Back to Toggl Global</MiniBarLink>
          <DropdownBaseTrigger item="global-products">
            <MiniBarAnchor tabIndex={0}>
              Products
              <IconArrowDownThin />
            </MiniBarAnchor>
          </DropdownBaseTrigger>

          <MiniBarAnchor href={withPrefix('/blog/')}>Blog</MiniBarAnchor>
          <MiniBarLink to={'/mission/'}>Our mission</MiniBarLink>
          <MiniBarLink to={'/jobs/'}>Working at Toggl</MiniBarLink>
        </MiniBarLinksSection>
      </MiniBar>

      <Navigation.Header
        alt={altNav}
        background={background}
        logoWidth={['140px', '168px']}
        logo={
          <Link to={'/track/'}>
            <Track />
          </Link>
        }
        secondaryLinks={
          <React.Fragment>
            {!altNav && (
              <Responsive.DesktopOnly>
                <DemoRequestLink
                  isOpen={isOpen}
                  to={'/track/demo-request/?source=header'}
                >
                  Book a demo
                </DemoRequestLink>
              </Responsive.DesktopOnly>
            )}
            {hasSession ? (
              <Button.Secondary disabled={loggingOut} onClick={onLogout}>
                Log out
              </Button.Secondary>
            ) : (
              <Link to={'/track/login/'}>
                <Button.Secondary tabIndex="-1">Log in</Button.Secondary>
              </Link>
            )}
          </React.Fragment>
        }
        cta={<TrackCta hasSession={hasSession} />}
      >
        {altNav ? <AltNavigation /> : <TrackHeaderContents />}
      </Navigation.Header>
    </Header>
  )
}

const TrackCta = ({ hasSession }: { hasSession: boolean }) => {
  const { isOpen } = useDropdown()
  return hasSession ? (
    <a href={process.env.GATSBY_TRACK_APP_URL}>
      <Responsive.DesktopOnly>
        {isOpen ? (
          <Button.Primary tabIndex="-1" reducedDevicePadding>
            Go to timer
          </Button.Primary>
        ) : (
          <Button.SecondaryTrack tabIndex="-1" reducedDevicePadding>
            Go to timer
          </Button.SecondaryTrack>
        )}
      </Responsive.DesktopOnly>
      <Responsive.MobileOnly>
        <Button.Primary tabIndex="-1" reducedDevicePadding>
          Go to timer
        </Button.Primary>
      </Responsive.MobileOnly>
    </a>
  ) : (
    <Link to={'/track/signup/'} className="cta--action">
      <Responsive.DesktopOnly>
        {isOpen ? (
          <Button.Primary tabIndex="-1" reducedDevicePadding>
            Try for free
          </Button.Primary>
        ) : (
          <Button.SecondaryTrack tabIndex="-1" reducedDevicePadding>
            Try for free
          </Button.SecondaryTrack>
        )}
      </Responsive.DesktopOnly>
      <Responsive.MobileOnly>
        <Button.Primary tabIndex="-1" reducedDevicePadding>
          Try for free
        </Button.Primary>
      </Responsive.MobileOnly>
    </Link>
  )
}

const AltNavigation = () => {
  return (
    <AltNav>
      <li>
        <Link to="/track/time-management/" activeClassName="active">
          Time Management
        </Link>
      </li>
      <Responsive.DesktopOnly>
        <span> | </span>
      </Responsive.DesktopOnly>
      <li>
        <Link to="/track/time-management-101/" activeClassName="active">
          The Basics
        </Link>
      </li>
      <li>
        <Link to="/track/time-management-skills/" activeClassName="active">
          Methods
        </Link>
      </li>
      <li>
        <Link to="/track/team-time-management/" activeClassName="active">
          For Teams
        </Link>
      </li>
      <li>
        <Link to="/track/project-time-management/" activeClassName="active">
          For Projects
        </Link>
      </li>
      <li>
        <Link to="/track/time-management-tools/" activeClassName="active">
          Resources
        </Link>
      </li>
    </AltNav>
  )
}

const RowWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  a {
    flex: 1;
  }
`

const Social = styled.div`
  flex: 100%;
  margin: 0 auto;
  svg {
    width: 28px;
    margin: auto;
  }
  a {
    margin: 10px 20px;
    &:hover svg {
      fill: ${(p) => p.theme.primary} !important;
    }
  }

  ${RowWrapper} a {
    flex: none;
  }
`

const MiniBarLink = styled(Link)`
  ${text.paragraph4};
  cursor: pointer;
  font-size: 0.75rem;
  color: ${colors.darkPurple};
  margin: 0 10px;
  &:hover {
    text-decoration: underline;
  }
`

const MiniBarAnchor = styled.a`
  ${text.paragraph4};
  align-items: center;
  color: ${colors.darkPurple};
  cursor: pointer;
  display: flex;
  font-size: 0.75rem;
  margin: 0 10px;

  :focus {
    outline-style: none;
  }

  :focus-visible {
    outline-style: auto;
  }

  :hover {
    text-decoration: underline;
  }
`

const MiniBarLinksSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-right: 42px;
  align-items: center;
`

const MiniBar = styled.div`
  display: none;
  pointer-events: all;
  ${media.mq[1]} {
    display: flex;
    justify-content: flex-end;

    height: 31px;
    background-color: ${(p) => p.theme.primary};
  }
`

const AltNav = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  list-style: none;
  li {
    font-family: ${fonts.inter};
    margin: 0 10px;
    position: relative;
    ${media.mq[0]} {
      font-size: 1rem;
    }
    ${media.mq[1]} {
      font-size: clamp(0.75rem, 1.5vw, 1rem);
      line-height: 1.1;
      flex: 0 0 auto;
    }
    & a.active,
    & a:hover {
      &:after {
        content: '';
        width: 100%;
        height: 1px;
        background-color: ${colors.pink};
        position: absolute;
        left: 0;
        bottom: -6px;
        transition: all linear 0.3s;
      }
    }
    &:first-of-type {
      font-family: ${fonts.gtHaptikMedium};
      font-size: 1rem;
      ${media.mq[2]} {
        margin-left: 0;
      }
    }
  }
  span {
    color: ${colors.white};
  }
  ${media.mq[1]} {
    flex-direction: row;
  }
  ${media.mq[2]} {
    margin: auto;
  }
`

const PlanLink = styled.a`
  &:hover {
    color: ${colors.violet};
  }
`

const HireLink = styled.a`
  &:hover span {
    color: ${colors.orange};
  }
`

const DemoRequestLink = styled(Link)`
  ${media.mq[1]} {
    margin-left: auto;
    border-right: 2px solid
      ${(p) => (p.isOpen ? colors.fadedPurple : colors.lightBeige)};
    ${text.defaultText};
    padding-right: 18px;
    &:hover {
      color: ${colors.pink};
    }
  }
`
